import { CustomRoute } from "@/interfaces";
import { AppWindowMac, Building2, DollarSign, LayoutDashboard, Settings, User, Users, Users2, FileText, CheckCircle, BarChart3 } from "lucide-react";
import React from "react";

const Dashboard = React.lazy(() => import("../modules/Admin/Dashboard"));
const Services = React.lazy(() => import("../modules/Admin/Services"));
const ClientManagement = React.lazy(() => import("../modules/Admin/ClientManagement/index"));
const VendorManagement = React.lazy(() => import("../modules/Admin/VendorManagement/index"));
const EmployeeManagement = React.lazy(() => import("../modules/Admin/EmployeeManagement/index"));
const PermissionTemplates = React.lazy(() => import("../modules/Admin/PermissionTemplates/index"));
const CommissionTemplates = React.lazy(() => import("../modules/Admin/CommissionManagement/TemplatesPage"));
const AllCommissions = React.lazy(() => import("../modules/Admin/CommissionManagement/AllCommissions"));
const CommissionPayouts = React.lazy(() => import("../modules/Admin/CommissionManagement/Payouts"));
const TeamworkProjects = React.lazy(() => import("../modules/Admin/TeamWorkProjects"));
const StripePayment = React.lazy(() => import("../modules/Admin/Billinginvoices/StripePayment"));
const EditProfilePage = React.lazy(() => import("../modules/Profile/EditProfile"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));
const AdminInvoices = React.lazy(() => import("../modules/Admin/Invoices/MyInvoices"));
const CreateAdminInvoice = React.lazy(() => import("../modules/Admin/Invoices/CreateInvoicePage"));
const AllInvoices = React.lazy(() => import("../modules/Admin/Invoices/AllInvoices"));
const InvoiceHistory = React.lazy(() => import("../modules/Admin/Invoices/InvoiceHistory"));
const NetSuiteVendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/AllBills"));
const QuickBooksVendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/QuickBooksAllBills"))
const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
const CreateBillPage = React.lazy(() => import("../modules/Admin/Bills/CreateBillPage"));
const RecurringInvoices = React.lazy(() => import("../modules/Admin/Invoices/RecurringInvoices"));
const Reports = React.lazy(() => import("../modules/SuperAdmin/Reports/index"));
import { useUserStore } from "@/stores/userStore";
import ApprovalManagement from "@/modules/Finance/ApprovalManagement";

/**
 * Get admin routes
 * @returns Array of admin routes
 */
const currentCompany = useUserStore.getState().currentCompany;
const isQuickBooks = currentCompany?.invoicingAccounting === "QuickBooks";

const getAdminRoutes = (): CustomRoute[] => [
  {
    path: "/",
    id: "Admin",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  {
    path: "/services",
    id: "Admin",
    element: <Services />,
    menuName: "Services",
    icon: Settings,
    highlightOn: ["services", "service"],
  },
  // Client Management - Note: Client CRUD (create/edit/delete) is SuperAdmin only.
  // This module is for managing client team members, services, Users, and commissions.
  {
    path: "/client-management",
    id: "Admin",
    element: <ClientManagement />,
    menuName: "Client Management",
    icon: User,
    highlightOn: ["companies", "company", "all-vendors"],
    submenu: [
      {
        path: "/client-management",
        id: "Admin",
        element: <ClientManagement />,
        menuName: "Client Management",
        icon: User,
        highlightOn: ["companies", "company"],
      },
      {
        path: "/projects",
        id: "Admin",
        menuName: "Projects",
        element: <TeamworkProjects />,
        icon: Building2,
        highlightOn: ["all-vendors"],
      }
    ]
  },
  {
    path: "/vendor-management",
    id: "Admin",
    element: <VendorManagement />,
    menuName: "Vendor Management",
    icon: Building2,
    highlightOn: ["VendorManagement", "organization"],
  },
  {
    path: "/employee-management",
    id: "Admin",
    element: <EmployeeManagement />,
    menuName: "Employee Management",
    icon: Users2,
    highlightOn: ["employees", "employee", "permission-templates", "employee-commissions"],
    submenu: [
      {
        path: "/employee-management",
        id: "Admin",
        element: <EmployeeManagement />,
        menuName: "All Employees",
        icon: Users2,
        highlightOn: ["employees", "employee"],
      },
      {
        path: "/employee-management/permission-templates",
        id: "Admin",
        element: <PermissionTemplates />,
        menuName: "Permission Templates",
        icon: Users2,
        highlightOn: ["permission-templates"],
      },
    ],
  },
  {
    path: "/commissions",
    id: "Admin",
    element: <AllCommissions />,
    menuName: "Commissions",
    icon: DollarSign,
    highlightOn: ["commissions", "commission-templates", "commission-payouts"],
    submenu: [
      {
        path: "/commissions/all",
        id: "Admin",
        element: <AllCommissions />,
        menuName: "All Commissions",
        icon: DollarSign,
        highlightOn: ["commissions-all"],
      },
      {
        path: "/commissions/payouts",
        id: "Admin",
        element: <CommissionPayouts />,
        menuName: "Payouts",
        icon: DollarSign,
        highlightOn: ["commission-payouts"],
      },
      {
        path: "/commissions/templates",
        id: "Admin",
        element: <CommissionTemplates />,
        menuName: "Templates",
        icon: DollarSign,
        highlightOn: ["commission-templates"],
      },
    ],
  },
  // {
  //   path: "/teamwork-projects",
  //   id: "Admin",
  //   menuName: "Teamwork Projects",
  //   element: <TeamworkProjects />,
  //   icon: Building2,
  //   highlightOn: ["all-vendors"],
  // },
  {
    path: "",
    id: "Admin",
    element: null,
    menuName: "Financials",
    icon: AppWindowMac,
    highlightOn: ["billing", "invoices"],
    submenu: [
      // Stripe Payment, All Invoices, Create Invoice, and My Invoices
      {
        path: "/billing/stripe-payment",
        id: "Admin",
        element: <StripePayment />,
        menuName: "Stripe Payment",
        icon: Building2,
        highlightOn: ["add-vendor"],
      },
      {
        path: "/billing/all-invoices",
        id: "Admin",
        element: <AllInvoices />,
        menuName: "All Invoices",
        icon: Building2,
        highlightOn: ["all-invoices"],
      },
      {
        path: "/netsuite/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="netsuite" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-approvals"],
      },
      {
        path: "/quickbooks/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="quickbooks" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["quickbooks-approvals"],
      },
      {
        path: "/billing/invoice-history",
        id: "Admin",
        element: <InvoiceHistory />,
        menuName: "Invoice History",
        icon: Building2,
        highlightOn: ["invoice-history"],
      },
      {
        path: "/invoices/create",
        id: "Admin",
        element: <CreateAdminInvoice />,
        menuName: "Create Invoice",
        icon: Building2,
        highlightOn: ["create-invoice"],
      },
      {
        path: "/billing/recurring-invoices",
        id: "Admin",
        element: <RecurringInvoices />,
        menuName: "Recurring Invoices",
        icon: Building2,
        highlightOn: ["recurring-invoices"],
        hideInMenu: !isQuickBooks,
      },
      {
        path: "/invoices",
        id: "Admin",
        element: <AdminInvoices />,
        menuName: "My Invoices",
        icon: Building2,
        highlightOn: ["invoices"],
      },
      {
        path: "/billing/vendor-bills",
        id: "Admin",
        element: currentCompany?.invoicingAccounting === "NetSuite" ? <NetSuiteVendorBills /> : <QuickBooksVendorBills />,
        menuName: "Vendor Bills",
        icon: FileText,
        highlightOn: ["vendor-bills"],
      },
      {
        path: "/billing/vendor-bill-approvals",
        id: "Admin",
        element: <VendorBillApprovals />,
        menuName: "Vendor Bills Approvals",
        icon: FileText,
        highlightOn: ["vendor-bill-approvals"],
      },
      {
        path: "/billing/create-bill",
        id: "Admin",
        element: <CreateBillPage />,
        menuName: "Create Bill",
        icon: FileText,
        highlightOn: ["create-bill"],
      },
    ],
  },
  {
    path: "/reports",
    id: "Admin",
    element: <Reports />,
    menuName: "Reports",
    icon: BarChart3,
    highlightOn: ["reports"],
  },
  {
    path: "/notifications",
    id: "Admin",
    element: <AllNotifications />,
    menuName: "Notifications",
    hideInMenu: true,
    highlightOn: ["notifications"],
  },
  {
    path: "/profile",
    id: "Admin",
    element: <EditProfilePage />,
    menuName: "Profile",
    icon: Users,
    highlightOn: ["profile"],
  },
];

// Export the function for dynamic route generation
export { getAdminRoutes };
